import { logout as logoutRequest, refreshToken } from "@/api/auth";
import {
  forgetImpersonation,
  readImpersonation,
  useAuthStore,
} from "@/stores/auth";
import { notify } from "@/lib/notify";

// Oturum sürekliliği httpOnly refresh çerezinde. Access token bellekte durduğu için sayfa
// yenilenince kayboluyor; açılışta ve 401'de burayı çağırıp geri alıyoruz.
//
// Tek uçuş: eşzamanlı üç istek 401 alırsa üç yenileme başlatmasın, hepsi aynı sözü beklesin.
let inFlight = null;

// Ağ hatası ile "oturum gerçekten bitti" AYNI ŞEY DEĞİL. İkisini `catch(() => null)` ile
// birleştirmek, çevrimdışı açılan paneli 30 günlük geçerli çerezi olan kullanıcıya giriş ekranı
// olarak gösteriyordu. `error.response` yoksa istek sunucuya hiç ulaşmadı.
const isDeadSession = (error) =>
  Boolean(error.response) && error.response.status === 401;

export const refreshSession = () => {
  inFlight =
    inFlight ??
    refreshToken()
      .then((data) => {
        if (!data?.token) {
          return null;
        }

        // setAuth DEĞİL: o `resetCache()` çağırıp bütün react-query önbelleğini siliyor ve
        // impersonation'ı düşürüyor. Sessiz yenileme aynı kullanıcının token'ını tazeliyor,
        // kimlik değişmiyor — önbellek durmalı.
        useAuthStore.getState().setToken(data.token);

        // setUser, updateUser DEĞİL: bu çağrı hem canlı oturumun sessiz tazelemesi hem de
        // sayfa yenilendikten sonraki İLK kimlik kaynağı. updateUser bir yama fonksiyonu
        // olduğu için user null'ken hiçbir şey yazmıyor, yani açılışta sessizce boşa
        // gidiyordu. Cevap kullanıcının tamamını taşıyor, yamaya gerek yok.
        if (data.user) {
          useAuthStore.getState().setUser(data.user);
        }

        return data.token;
      })
      .catch((error) => {
        if (!isDeadSession(error)) {
          console.warn("[session] yenileme ulaşamadı", error.message);
        }

        return null;
      })
      .finally(() => {
        inFlight = null;
      });

  return inFlight;
};

// Uygulama açılışı. Çerez varsa oturum geri gelir, yoksa giriş ekranı. Bu bitmeden yönlendirme
// yapılmıyor (store.status === 'loading'), yoksa her tam sayfa yenilemesinde oturumu açık olan
// kullanıcı bir an giriş ekranına düşerdi.
export const bootstrapSession = async () => {
  // persist kaldırıldı; eski sürümden kalan 24 saatlik JWT hâlâ localStorage'da duruyor olabilir.
  // Bu değişikliğin amacı tam olarak onu ortadan kaldırmaktı.
  localStorage.removeItem("noxus-auth");

  const token = await refreshSession();

  if (!token) {
    useAuthStore.getState().logout();

    return;
  }

  await restoreImpersonation();
};

// Yenilemeden önce bir partnerin içindeysek oraya geri dön. Çerez superadmin'in kendisine ait
// olduğu için refresh her zaman superadmin olarak dönüyor; kiracı token'ı burada yeniden
// alınıyor, yoksa kullanıcı kiracı sayfasında superadmin olarak kalıp 403 yiyordu.
const restoreImpersonation = async () => {
  const remembered = readImpersonation();
  const { user, impersonate } = useAuthStore.getState();

  if (!remembered || user?.role !== "superadmin") {
    return;
  }

  try {
    const { impersonateTenant } = await import("@/api/admin");
    const { token, tenant } = await impersonateTenant(remembered.id);

    impersonate({ token, tenant });
  } catch {
    // Partner silinmiş ya da yetki kalkmış olabilir: seçimi unut, superadmin olarak devam et.
    forgetImpersonation();
  }
};

// Çıkış: sunucudaki oturum ailesini kapat ve çerezi sil.
//
// Sunucuya ULAŞAMADIYSAK yerel durumu temizlemiyoruz. Oturum artık localStorage'da değil
// çerezde; "çıktım" deyip ekranı boşaltmak, çerez 30 gün ayakta dururken kullanıcıya yanlış
// güvence vermek olurdu — paylaşılan şube terminalinde tam olarak önemli olan senaryo bu.
export const endSession = async () => {
  try {
    await logoutRequest();
  } catch (error) {
    if (!error.response) {
      notify.error(
        "Çıkış yapılamadı — bağlantı kurulamadı. Oturumun hâlâ açık, tekrar dene.",
      );

      return false;
    }

    // Sunucu cevap verdi ama hata döndü: çerez durumu belirsiz, yine de yerelde kapatıyoruz.
    console.error("[session] çıkış reddedildi", error.response.status);
  }

  useAuthStore.getState().logout();

  return true;
};
